import axios from 'axios';
import { pick } from 'lodash';
import Router from 'next/router';
import { pushAlert } from 'redux/actions/alertActions';
import { logout } from 'redux/actions/authActions';
import { getActiveToken } from '../../helpers/authToken';
import { getPusherSocketId } from '../../lib/pusherClient';
import { useToastStore } from '../../components/ToastManager/useToasts';
import { API, API_END, API_START } from '../actions/types';

// Read the active locale without throwing in the App Router, where the next/router singleton is never mounted.
const getLocale = () => Router.router?.locale || 'de';

/**
 * Attach an explicit bearer header for calls to our own API.
 *
 * Historically this middleware sent no Authorization header at all and relied on the `token`
 * cookie plus the CookieAuthentication middleware on the backend. Inside a third-party iframe
 * that cookie is blocked or partitioned, so every write silently degraded to a guest request.
 *
 * Scoped to our own API host so a token can never leak to a third party, and omitted entirely
 * when there is no token — outside the embed getActiveToken() reads the same cookie as before,
 * which keeps the behaviour unchanged.
 */
const socketHeader = (url) => {
    if (url.includes('http') && !url.startsWith(process.env.NEXT_PUBLIC_API_HOST)) return {};
    const socketId = getPusherSocketId();
    return socketId ? { 'X-Socket-Id': socketId } : {};
};

const authHeaders = (url) => {
    if (url.includes('http') && !url.startsWith(process.env.NEXT_PUBLIC_API_HOST)) return {};

    const token = getActiveToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
};

// Navigate in a way that works in both the Pages Router (next/router) and the App Router (full-page navigation).
const safeRedirect = (path) => {
    if (Router.router) {
        Router.replace(path);
    } else if (typeof window !== 'undefined') {
        window.location.replace(path);
    }
};

const apiMiddleware =
    ({ dispatch, getState }) =>
    (next) =>
    (action) => {
        next(action);

        if (action.type !== API) return;

        const {
            url,
            method = 'GET',
            data,
            onSuccess,
            onFailure,
            onFinish,
            onBefore,
            onError,
            getValidation,
            headers,
            muted,
            muteWhen,
            toastCallback,
            keepSessionOnUnauthorized,
            ...rest
        } = action.payload;

        const dataOrParams = ['GET', 'DELETE'].includes(method) ? 'params' : 'data';
        axios.defaults.headers.common['Accept'] = 'application/json';
        axios.defaults.headers.common['Content-Language'] = getLocale();

        dispatch({ type: API_START });
        axios
            .request({
                url: url.includes('http') ? url : process.env.NEXT_PUBLIC_API_HOST + url,
                method,
                // The socket this tab listens on, so a broadcast about this
                // write can leave its author out (`toOthers()` server-side).
                // Absent until a screen has subscribed to something.
                headers: { ...authHeaders(url), ...socketHeader(url), ...headers },
                [dataOrParams]: data,
                ...rest
            })
            .then((response) => {
                if (response.status === 503) {
                    safeRedirect('/maintenance');
                    return;
                }

                if (response.data?.type === 'confirmation') {
                    useToastStore.getState().addToast({
                        ...pick(response.data.data ?? {}, ['title', 'type', 'description']),
                        data: response.data.data,
                        type: 'confirmation',
                        callback: toastCallback ?? (() => onSuccess?.(response.data, response))
                    });
                }

                if (response.data?.type === 'toast') {
                    useToastStore.getState().addToast({ ...response.data.data, callback: toastCallback });
                }

                if (onBefore) onBefore();
                if (onSuccess) {
                    onSuccess(response.data, response);
                } else {
                    // if (!muted) dispatch(pushAlert("Erfolg!", "success"));
                }
            })
            .catch((error) => {
                if (error.response?.status === 503) {
                    safeRedirect('/maintenance');
                    return;
                }

                // A caller holding work the server does not have yet (the CMS editor)
                // takes the 401 itself: logging out here unmounts it and discards that
                // work, while the person can just as well sign in again in another tab
                // and retry.
                if (error.response?.status === 401 && !keepSessionOnUnauthorized) {
                    dispatch(logout());
                    safeRedirect('/');
                    return;
                }

                // parse blob request into json
                if (error?.response?.data instanceof Blob) {
                    error?.response?.data.text().then((data) => {
                        error.response.data = JSON.parse(data);
                        handleError(error);
                    });
                } else {
                    handleError(error);
                }
            })
            .finally(() => {
                dispatch({ type: API_END });
                if (onFinish) {
                    onFinish();
                }
            });

        const handleError = (error) => {
            if (onError) onError(error);
            if (onBefore) onBefore();
            // Cancelled by user
            if (axios.isCancel(error)) {
                // dispatch(pushAlert("Durch Benuter unterbrochen!", "warning"));
            } else if (error.response) {
                if (onFailure) {
                    onFailure(error.response);
                }

                if (error.response.status === 422) {
                    const fieldErrors = error.response.data?.errors;
                    const hasFieldErrors = fieldErrors && Object.keys(fieldErrors).length > 0;
                    if (!muted && !muteWhen?.(error.response)) {
                        if (!hasFieldErrors && error.response.data?.message) {
                            // Business-rule 422 (no field bag): show the backend's German message.
                            dispatch(pushAlert(error.response.data.message, 'danger'));
                        } else {
                            dispatch(pushAlert('Bitte überprüfe deine Eingaben auf Fehler.', 'danger'));
                        }
                    }
                    if (getValidation) {
                        getValidation(handleValidation(error));
                    }
                } else if (error.response.status === 403 || error.response.status === 409) {
                    // Missing-permission denials while loading data are handled inline (or silently),
                    // never as a toast — only surface genuine 403/409 conflicts to the user.
                    const isPermissionDenied = error.response.data?.error === 'App\\Exceptions\\Club\\PermissionDenied';
                    if (!muted && !isPermissionDenied) dispatch(pushAlert(error.response.data.message, 'danger'));
                } else if (error.response.status === 401) {
                    if (!muted)
                        dispatch(
                            pushAlert('Du bist nicht eingeloggt, bitte logge dich ein um fortzufahren!', 'warning')
                        );

                    // dispatch(logout());
                } else if (error.response.status === 429) {
                    if (!muted)
                        dispatch(
                            pushAlert(
                                'Du hast leider zu viele Anfragen versendet, bitte warte eine kurze Zeit!',
                                'danger'
                            )
                        );
                } else {
                    let message = 'Es ist ein unbekannter Fehler aufgetreten!';
                    if (process.env.NODE_ENV === 'development' && error.response.data && error.response.data.message) {
                        message += ' (' + error.response.data.message + ')';
                    }

                    if (!muted) dispatch(pushAlert(message, 'danger'));
                }
            } else {
                if (onFailure) {
                    onFailure(error);
                } else {
                    if (process.env.NODE_ENV === 'development') console.debug(error);
                    dispatch(pushAlert('Es gibt ein Problem mit deiner Internetverbindung!', 'danger'));
                }
            }
        };
    };

const handleValidation = (error) => {
    let validationErrors = error.response.data.errors;
    var errorObj = {};
    if (validationErrors)
        Object.keys(validationErrors).forEach(function (key) {
            errorObj = { ...errorObj, [key]: validationErrors[key][0] };
        });

    return errorObj;
};

export default apiMiddleware;
